import React, { useState, useMemo } from 'react';
import {
  Divider,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import styled from 'theme/styled';
import p from 'translation';
import useDraft from 'core/hooks/draft';
import EditProfilePicture from 'apps/manage/components/edit-profile-picture';
import useSnackbar from 'components/snackbar';
import { useHistory } from 'react-router';
import useAuthenticated from 'core/data/authenticated';
import Avatar from 'core/components/avatar';
import PasswordTextField from 'core/components/password-text-field';
import { signup } from './actions';
import { useUserFromCache } from '../../user/context';

// see FusionAuth tenant security settings for complexity requirements
const MINIMUM_PASSWORD_LENGTH = 8;

interface Field<T = string> {
  touched: boolean;
  value: T;
}

interface SignupData {
  firstName: Field;
  lastName: Field;
  email: Field;
  password: Field;
  repeatedPassword: Field;
  receiveEmails: Field<boolean>;
}

interface ValidatorReturn {
  error?: boolean;
  helperText?: string;
}
type Validator = (value: unknown) => ValidatorReturn;

function validate(field: Field, validator: Validator): ValidatorReturn {
  if (!field.touched) return { error: false };
  return validator(field.value);
}

function isString(value: unknown): value is string {
  return !!value && typeof value === 'string';
}

function failedIsRequiredReturn(field: string): ValidatorReturn {
  return {
    error: true,
    helperText: p.t('common.form-validation.is-required', { field }),
  };
}

function isRequired(field: string): Validator {
  return (value: unknown) =>
    isString(value) ? {} : failedIsRequiredReturn(field);
}

function isRequiredLength(field: string, length: number): Validator {
  return (value: unknown) => {
    if (!isString(value)) {
      return failedIsRequiredReturn(field);
    }

    if (value.length < length) {
      return {
        error: true,
        helperText: p.t('common.form-validation.required-length', {
          field,
          length,
        }),
      };
    }
    return {};
  };
}

function isEmail(field: string): Validator {
  return (value: unknown) => {
    if (!isString(value)) {
      return failedIsRequiredReturn(field);
    }

    if (!value.includes('@')) {
      return {
        error: true,
        helperText: p.t('common.form-validation.email-missing-at', { field }),
      };
    }
    return {};
  };
}

function doesMatch(field: string, match: string): Validator {
  return (value: unknown) => {
    if (!isString(value)) {
      return failedIsRequiredReturn(field);
    }

    if (value !== match) {
      return {
        error: true,
        helperText: p.t('common.form-validation.does-not-match', { field }),
      };
    }

    return {};
  };
}

type Validation = Partial<Record<keyof SignupData, ValidatorReturn>>;
function validateDraft(draft: SignupData): Validation {
  return {
    firstName: validate(draft.firstName, isRequired(p.t('common.first-name'))),
    lastName: validate(draft.lastName, isRequired(p.t('common.last-name'))),
    email: validate(draft.email, isEmail(p.t('common.email'))),
    password: validate(
      draft.password,
      isRequiredLength(p.t('common.password'), MINIMUM_PASSWORD_LENGTH),
    ),
    repeatedPassword: validate(
      draft.repeatedPassword,
      doesMatch(p.t('common.passwords'), draft.password.value),
    ),
  };
}

function isTouched(draft: SignupData): boolean {
  return Object.values(draft).every(value => value?.touched);
}

function isValid(validation: Validation): boolean {
  return Object.values(validation).every(value => !value?.error);
}

export default function CreateProfile(): JSX.Element {
  const { queue } = useSnackbar();
  const history = useHistory();
  const [, setAuthenticated] = useAuthenticated();
  const user = useUserFromCache();
  const [draft, update] = useDraft<SignupData>({
    firstName: {
      value: user.firstName,
      touched: !!user.firstName,
    },
    lastName: {
      value: user.lastName,
      touched: !!user.lastName,
    },
    email: {
      value: user.email,
      touched: !!user.email,
    },
    password: {
      value: '',
      touched: false,
    },
    repeatedPassword: {
      value: '',
      touched: false,
    },
    receiveEmails: {
      value: false,
      touched: true,
    },
  });
  const [profilePicture, setProfilePicture] = useState<Blob | undefined>(
    undefined,
  );

  const validation = useMemo(() => validateDraft(draft), [draft]);
  const profilePictureURL = useMemo(
    () => (profilePicture ? URL.createObjectURL(profilePicture) : undefined),
    [profilePicture],
  );

  return (
    <Container>
      <section>
        <h1>{p.t('pages.onboarding.create-personal-profile.title')}</h1>
        <p>{p.t('pages.onboarding.create-personal-profile.description')}</p>
      </section>

      <form
        onSubmit={async e => {
          e.stopPropagation();
          e.preventDefault();

          const ok = await signup(
            {
              firstName: draft.firstName.value,
              lastName: draft.lastName.value,
              email: draft.email.value,
              password: draft.password.value,
              receiveEmails: draft.receiveEmails.value,
              secret: user.secret,
            },
            profilePicture,
          );

          if (!ok) {
            queue({ message: 'Could not signup. Please try again' });
            return;
          }

          queue({ message: 'Created your profile!' });
          setAuthenticated('authenticated');
          history.replace('/app/manage');
          window.location.reload();
        }}
      >
        <section>
          <header>
            <h2>
              {p.t(
                'pages.onboarding.create-personal-profile.personal-information',
              )}
            </h2>
            <Divider />
          </header>

          <div className='profile-picture-container'>
            <EditProfilePicture
              onSelect={async img => setProfilePicture(img)}
              canEdit
            >
              <ProfilePicture
                user={{
                  firstName: draft.firstName.value,
                  lastName: draft.lastName.value,
                  profilePicture: profilePictureURL
                    ? { url: profilePictureURL }
                    : undefined,
                }}
              />
            </EditProfilePicture>
          </div>
          <div className='input-container'>
            <TextField
              variant='standard'
              {...validation.firstName}
              id='firstName'
              label={p.t('common.first-name')}
              value={draft.firstName.value}
              onChange={e =>
                update({ firstName: { value: e.target.value, touched: true } })
              }
            />
          </div>
          <div className='input-container'>
            <TextField
              variant='standard'
              {...validation.lastName}
              id='lastName'
              label={p.t('common.last-name')}
              value={draft.lastName.value}
              onChange={e =>
                update({ lastName: { value: e.target.value, touched: true } })
              }
            />
          </div>
          <div className='input-container'>
            <TextField
              variant='standard'
              {...validation.email}
              id='email'
              label={p.t('common.email')}
              type='email'
              value={draft.email.value}
              disabled
            />
          </div>
        </section>

        <section>
          <header>
            <h2>
              {p.t(
                'pages.onboarding.create-personal-profile.create-a-password',
              )}
            </h2>
            <Divider />
          </header>

          <div className='input-container'>
            <PasswordTextField
              {...validation.password}
              id='password'
              label={p.t('common.password')}
              value={draft.password.value}
              onBlur={() =>
                update({
                  password: { value: draft.password.value, touched: true },
                })
              }
              onChange={password =>
                update({ password: { value: password, touched: false } })
              }
              fullWidth={false}
            />
          </div>
          <div className='input-container'>
            <PasswordTextField
              {...validation.repeatedPassword}
              id='repeat-password'
              label={p.t('common.repeat-password')}
              value={draft.repeatedPassword.value}
              onChange={repeatedPassword =>
                update({
                  repeatedPassword: { value: repeatedPassword, touched: true },
                })
              }
              fullWidth={false}
            />
          </div>
        </section>

        <section>
          <FormControlLabel
            label={p.t('pages.onboarding.create-personal-profile.i-agree')}
            control={
              <Checkbox
                color='primary'
                checked={draft.receiveEmails.value}
                onChange={e =>
                  update({
                    receiveEmails: {
                      value: e.target.checked,
                      touched: true,
                    },
                  })
                }
              />
            }
          />
        </section>

        <section>
          <Button
            type='submit'
            variant='contained'
            color='primary'
            disabled={!isTouched(draft) || !isValid(validation)}
          >
            {p.t('pages.onboarding.create-personal-profile.create-account')}
          </Button>
        </section>
      </form>
    </Container>
  );
}

const Container = styled.main`
  header {
    margin-bottom: 1em;
  }
  form {
    margin-top: 1em;
  }

  .input-container {
    margin-bottom: 1em;
  }

  .profile-picture-container {
    margin: 2em 0;
  }

  section {
    margin-top: 3em;
  }
`;

const ProfilePicture = styled(Avatar)`
  width: 4rem;
  height: 4rem;
`;
